import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomPageManager from "./CustomPageManager";
import CustomPageRenderer from "./CustomPageRenderer";
import { createCustomPageApi } from './customPageApi';
const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const sectionFactory = {
  header: () => ({ type: "header", title: "", subtitle: "" }),
  text: () => ({ type: "text", content: "" }),
  image: () => ({ type: "image", imageUrl: "", link: "" }),
  imageText: () => ({ type: "imageText", imageUrl: "", content: "", link: "" }),
  imageRow: () => ({ type: "imageRow", images: ["", "", ""], links: ["", "", ""] }),
  button: () => ({ type: "button", label: "", link: "" }),
  eventsCalendar: () => ({ type: "eventsCalendar", events: [{ title: "", date: "", time: "", location: "", link: "" }] }),
};

const defaultPage = () => ({
  title: "",
  slug: "",
  sections: [sectionFactory.header()],
  published: false,
});

const CustomPage = ({ contentApi, groupPublished }) => {
  const navigate=useNavigate(),api=useMemo(()=>createCustomPageApi(contentApi),[contentApi]);
  const [pages,setPages]=useState([]),[currentPage,setCurrentPage]=useState(defaultPage()),[baseline,setBaseline]=useState(null),[version,setVersion]=useState('');
  const [isEditing,setIsEditing]=useState(false),[status,setStatus]=useState(''),[error,setError]=useState(''),[listError,setListError]=useState('');
  const [loading,setLoading]=useState(false),[ready,setReady]=useState(false),[pending,setPending]=useState(false),[uploadingSection,setUploadingSection]=useState('');
  const builderRef=useRef(null),lock=useRef(false),alive=useRef(true),loadSequence=useRef(0);
  const dirty=isEditing&&JSON.stringify(currentPage)!==baseline;
  const activeSlug=isEditing?currentPage.slug:'';
  const canLeave=()=>!lock.current&&(!dirty||window.confirm('Discard your unsaved Custom Page changes?'));
  useEffect(()=>{const unload=e=>{if(dirty||lock.current){e.preventDefault();e.returnValue='';}};const leave=e=>{if(lock.current||(dirty&&!window.confirm('Discard your unsaved Custom Page changes?')))e.preventDefault();};window.addEventListener('beforeunload',unload);window.addEventListener('bookish:before-navigation',leave);return()=>{window.removeEventListener('beforeunload',unload);window.removeEventListener('bookish:before-navigation',leave);};},[dirty]);
  const refresh=useCallback(async()=>{const sequence=++loadSequence.current;setReady(false);setListError('');try{const r=await api.list();if(alive.current&&sequence===loadSequence.current){setPages(r.pages);setVersion(r.version);setPending(r.cleanupPending);setReady(true);}}catch(e){if(alive.current&&sequence===loadSequence.current)setListError(e.message);}},[api]);
  useEffect(()=>{alive.current=true;refresh();return()=>{alive.current=false;};},[refresh]);
  const run=async work=>{if(lock.current)return;lock.current=true;setLoading(true);setStatus('');setError('');try{await work();}catch(e){if(alive.current)setError(e.message);}finally{lock.current=false;if(alive.current)setLoading(false);}};
  const openBuilder=()=>{setIsEditing(true);window.requestAnimationFrame(()=>builderRef.current?.scrollIntoView({behavior:'smooth',block:'start'}));};
  const createPage=()=>{if(!ready||!canLeave())return;const page={...defaultPage(),version:contentApi?'new':version};setCurrentPage(page);setBaseline(JSON.stringify(page));setStatus('');setError('');openBuilder();};
  const loadPage=slug=>{if(!canLeave())return;run(async()=>{const p=await api.get(slug);if(!alive.current)return;const page={...p,title:p.title||p.pageTitle||'',sections:p.sections.map(s=>({...s,imageUrl:s.imageUrl||s.url||''})),slug};setCurrentPage(page);setBaseline(JSON.stringify(page));openBuilder();});};
  const deletePage=slug=>{if(!canLeave()||!window.confirm('Delete this page and remove its public link?'))return;run(async()=>{const expected=contentApi?(currentPage.slug===slug?currentPage.version:pages.find(p=>p.slug===slug)?.version):version;const r=await api.remove(slug,expected);if(!alive.current)return;setVersion(r.version);setPending(r.cleanupPending);setPages(prev=>prev.filter(p=>p.slug!==slug));if(currentPage.slug===slug){setIsEditing(false);setCurrentPage(defaultPage());}setStatus(r.cleanupPending?'Page deleted. Finish the pending update to remove old public files.':'Page deleted.');});};
  const savePage=published=>run(async()=>{const slug=currentPage.slug||slugify(currentPage.title);if(!slug||!currentPage.title.trim())throw Error('Enter a page title containing letters or numbers.');if(!currentPage.slug&&pages.some(p=>p.slug===slug))throw Error('That page address is already in use. Choose a different title for the new page.');const r=await api.save({...currentPage,slug,published},!currentPage.slug);if(!alive.current)return;const saved={...r.page,version:r.version};setCurrentPage(saved);setBaseline(JSON.stringify(saved));setVersion(r.version);setPending(r.cleanupPending);setPages(prev=>[...prev.filter(p=>p.slug!==slug),r.page].sort((a,b)=>a.title.localeCompare(b.title)));setStatus(r.cleanupPending?'Page saved. Finish the pending update to remove old copies.':published?(contentApi&&!groupPublished?'Page saved for publication. It becomes public when your group website is published.':'Custom page published successfully.'):'Custom page saved as draft.');});
  const updateSection=(idx,updater)=>setCurrentPage(prev=>({...prev,sections:prev.sections.map((s,i)=>i===idx?(typeof updater==='function'?updater(s):updater):s)}));
  const addSection=type=>setCurrentPage(prev=>({...prev,sections:[...prev.sections,{...sectionFactory[type](),id:crypto.randomUUID()}]}));
  const removeSection=idx=>{if(!window.confirm('Remove this section?'))return;setCurrentPage(prev=>({...prev,sections:prev.sections.filter((_,i)=>i!==idx)}));};
  const moveSection=(idx,direction)=>setCurrentPage(prev=>{const target=idx+direction;if(target<0||target>=prev.sections.length)return prev;const sections=[...prev.sections];[sections[idx],sections[target]]=[sections[target],sections[idx]];return {...prev,sections};});
  const handleSectionImageUpload=(idx,imageIdx,file)=>{if(!file)return;run(async()=>{setUploadingSection(imageIdx==null?String(idx):`${idx}-${imageIdx}`);try{const url=await api.upload(file);if(!alive.current)return;updateSection(idx,s=>s.type==='imageRow'?{...s,images:s.images.map((v,i)=>i===imageIdx?url:v)}:{...s,imageUrl:url});setStatus('Image added. Save the page to keep this change.');}finally{if(alive.current)setUploadingSection('');}});};
  const pageForPreview=currentPage;
  return (
    <div className="min-h-screen text-bookish-royal p-2 sm:p-6 md:p-10">
      <div className="max-w-6xl mx-auto space-y-6">
        {contentApi && <header className="rounded-2xl border border-bookish-purple/20 bg-bookish-cream p-6 shadow-sm">
          <p className="text-sm uppercase tracking-widest">Your publishing circle</p>
          <h1 className="mt-2 text-3xl font-serif text-bookish-purple">More pages, more possibilities</h1>
          <p className="mt-3">Make room for reading lists, gatherings, and the stories behind your books. Arrange each page a section at a time.</p>
          <p className="mt-2 text-sm">Draft pages stay private. Published pages appear only while your group website is published.</p>
        </header>}
        <CustomPageManager
          pages={pages}
          activeSlug={activeSlug}
          onCreate={createPage}
          disabled={loading || !ready || pending}
          onEdit={loadPage}
          onDelete={deletePage}
        />

        {!ready && !listError && <p role="status">Loading your custom pages…</p>}
        {listError && <p role="alert">{listError}</p>}
        <button disabled={loading} onClick={refresh} className="rounded-lg border border-bookish-purple/30 px-4 py-2">Reload page list (keep edits)</button>
        {pending && <div role="alert" className="rounded-xl border border-amber-300 p-4">An update is saved, but old files still need cleanup. <button disabled={loading} onClick={()=>run(async()=>{const r=await api.resume(version);if(!alive.current)return;setPending(r.cleanupPending);setStatus(r.cleanupPending?'Cleanup is still pending. Please retry.':'Publication update finished.');})}>Finish publication update</button></div>}
        {status && <p role="status">{status}</p>}
        {error && <p role="alert" className="text-red-700">{error}</p>}
        {isEditing && (
          <div
            ref={builderRef}
            className="bg-bookish-white/70 border border-bookish-purple/20 rounded-2xl p-3 sm:p-5 md:p-8 space-y-5 shadow-sm"
          >
            <h1 className="text-2xl font-serif font-bold text-bookish-purple">Custom Page Builder</h1>

            <fieldset disabled={loading || !ready || pending} className="space-y-5 min-w-0">
            <div className="space-y-2">
              <label htmlFor="custom-page-title" className="block text-sm font-semibold">Page Title</label>
              <input
                id="custom-page-title" maxLength={200}
                value={currentPage.title}
                onChange={(e) =>
                  setCurrentPage((prev) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                className="w-full border border-bookish-purple/20 rounded-lg px-3 py-2 bg-white"
                placeholder="Between the Pages"
              />
            </div>

            {currentPage.slug && <p className="text-sm break-words">Page address: {currentPage.slug} (stays the same when you rename the page)</p>}
            <div className="border border-bookish-purple/20 rounded-2xl overflow-hidden bg-bookish-white/80">
              <div className="px-4 py-3 border-b border-bookish-purple/15 bg-bookish-white/70">
                <h2 className="text-sm font-semibold tracking-wide uppercase">Page Preview</h2><p className="mt-1 text-sm">Preview your edits here. Save or publish to keep your changes.</p>
              </div>

              <div className="min-h-[55vh] p-1 sm:p-3 md:p-6 bg-bookish-cream/60">
                <CustomPageRenderer
                  page={pageForPreview}
                  editable
                  onSectionChange={updateSection}
                  onDeleteSection={removeSection}
                  onMoveSectionUp={(idx) => moveSection(idx, -1)}
                  onMoveSectionDown={(idx) => moveSection(idx, 1)}
                  onImageUpload={handleSectionImageUpload}
                  uploadingSection={uploadingSection}
                />
              </div>

              <div className="px-4 py-3 border-t border-bookish-purple/15 bg-white">
                <p className="text-xs uppercase tracking-wide text-bookish-purpleAccent mb-2">Add Section Toolbar · {currentPage.sections.length}/100 sections</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    ["header", "+ Header"],
                    ["text", "Text"],
                    ["image", "Image"],
                    ["imageText", "Image + Text"],
                    ["imageRow", "3 Images"],
                    ["button", "Button"],
                    ["eventsCalendar", "Events Calendar"],
                  ].map(([type, label]) => (
                    <button
                      key={type}
                      disabled={currentPage.sections.length >= 100}
                      onClick={() => addSection(type)}
                      className="px-3 py-2 rounded-full border border-bookish-purple/20 hover:bg-bookish-purple/5 text-sm"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <button
                onClick={() => savePage(false)}
                disabled={loading}
                className="px-5 py-2 rounded-md border border-bookish-purple/30 bg-white hover:bg-bookish-purple/5 disabled:opacity-50"
              >
                {loading ? "Working…" : currentPage.published ? "Unpublish and save draft" : "Save Draft"}
              </button>

              <button
                onClick={() => savePage(true)}
                disabled={loading}
                className="px-5 py-2 rounded-md bg-bookish-royal text-white hover:bg-bookish-royal/90 disabled:opacity-50"
              >
                {loading ? "Working…" : currentPage.published ? "Save published changes" : "Publish"}
              </button>

              <button
                onClick={() => { if(canLeave())navigate(contentApi ? "/publishing-group-portal" : "/authorportal/mainpage?tab=home"); }}
                className="px-5 py-2 rounded-md border border-bookish-purple/20 hover:bg-bookish-purple/5"
              >
                Back to Portal
              </button>
            </div>

            </fieldset>
            <button disabled={loading} onClick={()=>{if(canLeave()){setIsEditing(false);setCurrentPage(defaultPage());}}} className="rounded-lg border px-4 py-2">Cancel editing</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomPage;
